import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { getDashboardOverview } from '../services/dashboardService';
import usePunchPointStore from '../store/punchPointStore';
import useUIStore from '../store/uiStore';
import useAuthStore from '../store/authStore';

// Ask Komal is rolled out to HOD/CEO/HR only for now - same set as
// require_ai_access in app/routes/ai_routes.py and the /ai-assistant
// route's allowedRoles in App.js. Widen when it's ready for everyone.
const AI_ROLES = ['HOD', 'CEO', 'HR'];

const StatCard = ({ label, value, tone = 'default' }) => {
  const toneClasses = {
    default: 'border-komal-blue',
    danger: 'border-red-500',
    warning: 'border-amber-500',
  };
  return (
    <div className={`glass-card p-5 border-l-4 ${toneClasses[tone]}`}>
      <div className="text-3xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-500 mt-1">{label}</div>
    </div>
  );
};

const DashboardPage = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const { overduePunchPoints, fetchOverdue } = usePunchPointStore();
  const showToast = useUIStore((s) => s.showToast);
  const { user } = useAuthStore();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getDashboardOverview();
        setOverview(res.data.overview);
      } catch (err) {
        showToast(err.response?.data?.detail || 'Failed to load dashboard', 'error');
      }
      await fetchOverdue();
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <Link
            to="/punch-points/create"
            className="bg-komal-blue hover:bg-komal-blue-dark text-white text-sm font-semibold px-4 py-2 rounded"
          >
            + New Task / Issue
          </Link>
        </div>

        {AI_ROLES.includes(user?.role) && (
          <Link
            to="/ai-assistant"
            className="glass-card p-4 mb-6 flex items-center justify-between hover:bg-white/5 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="bg-komal-blue/10 text-komal-blue rounded-full p-2.5">
                <Sparkles size={18} />
              </div>
              <div>
                <div className="font-semibold text-gray-900 text-sm">Ask Komal</div>
                <div className="text-xs text-gray-500">
                  Overdue tasks, delay summaries, performance, or upload a document for review
                </div>
              </div>
            </div>
            <span className="text-komal-blue text-sm font-semibold group-hover:underline">Open →</span>
          </Link>
        )}

        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <StatCard label="Open Tasks/Issues" value={overview?.open_punch_points ?? '—'} />
              <StatCard label="Overdue" value={overview?.overdue_punch_points ?? '—'} tone="danger" />
              <StatCard label="Open Delays" value={overview?.open_delays ?? '—'} tone="warning" />
              <StatCard label="Escalated Delays" value={overview?.escalated_delays ?? '—'} tone="danger" />
              <StatCard label="Active Projects" value={overview?.active_projects ?? '—'} />
            </div>

            <div className="glass-card">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">Overdue Tasks/Issues</h2>
                <Link to="/punch-points" className="text-sm text-komal-blue hover:underline">
                  View all
                </Link>
              </div>
              {overduePunchPoints.length === 0 ? (
                <p className="text-gray-500 text-sm p-5">Nothing overdue right now. 🎉</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-100">
                      <th className="px-5 py-2 font-medium">Issue</th>
                      <th className="px-5 py-2 font-medium">Severity</th>
                      <th className="px-5 py-2 font-medium">Days Overdue</th>
                      <th className="px-5 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overduePunchPoints.map((pp) => (
                      <tr key={pp.id} className="border-b border-gray-50 last:border-0">
                        <td className="px-5 py-3">{pp.issue_description}</td>
                        <td className="px-5 py-3 capitalize">{pp.severity}</td>
                        <td className="px-5 py-3 text-red-600 font-semibold">{pp.days_overdue}</td>
                        <td className="px-5 py-3 capitalize">{pp.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
